import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "use-debounce";
import { UseQueryResult } from "@tanstack/react-query";

export type SelectSearchAsyncOption = {
  label: string;
  value: string;
  [key: string]: string | string[];
};

interface SelectSearchAsyncProps<T, TQueryParams = string> {
  queryFn: (filter: TQueryParams) => UseQueryResult<{ results: T[] }>;
  mapResultToOption: (item: T) => SelectSearchAsyncOption;
  queryParamsFn?: (filter: string) => TQueryParams;
  itemRenderer?: (item: SelectSearchAsyncOption) => React.ReactNode;
  filterQueryResultFn?: (value: T, index: number, array: T[]) => boolean;
  value?: string;
  onChange: (
    value:
      | SelectSearchAsyncOption
      | React.SetStateAction<SelectSearchAsyncOption | undefined>,
  ) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  triggerClassName?: string;
}

function SelectSearchAsync<T, TQueryParams = string>({
  queryFn,
  queryParamsFn,
  mapResultToOption,
  itemRenderer,
  filterQueryResultFn,
  value,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search ...",
  emptyMessage = "No results found",
  className,
  disabled = false,
  triggerClassName,
}: SelectSearchAsyncProps<T, TQueryParams>) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const [debouncedFilter] = useDebounce(inputValue, 500);

  const { data, isLoading } = queryFn(
    queryParamsFn
      ? queryParamsFn(debouncedFilter)
      : (debouncedFilter as TQueryParams),
  );
  const options = React.useMemo(() => {
    if (!data?.results || !Array.isArray(data.results)) return [];
    let filteredResults = data.results;
    if (filterQueryResultFn) {
      filteredResults = data.results.filter(filterQueryResultFn);
    }
    return filteredResults.map(mapResultToOption);
  }, [data, mapResultToOption, filterQueryResultFn]);

  const selectedOption = React.useMemo(() => {
    return (
      options.find((option) => option.value === value) ||
      (value ? { label: value, value } : undefined)
    );
  }, [options, value]);

  const handleInputChange = React.useCallback((value: string) => {
    setInputValue(value);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
            triggerClassName,
          )}
        >
          {selectedOption ? (
            <span>{selectedOption.label}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("min-w-[200px] p-0", className)}>
        <Command shouldFilter={false} value={value}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={inputValue}
            onValueChange={handleInputChange}
            className="h-9 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : options.length === 0 ? (
              <CommandEmpty>
                {debouncedFilter.trim() !== ""
                  ? emptyMessage
                  : "Type to search..."}
              </CommandEmpty>
            ) : (
              <CommandGroup className="max-h-60 overflow-auto">
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value} // Explicitly set value to make it unique
                    onSelect={() => {
                      onChange(option);
                      setOpen(false);
                    }}
                    className="flex items-center"
                  >
                    {itemRenderer ? (
                      itemRenderer(option)
                    ) : (
                      <>
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === option.value
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {option.label}
                      </>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { SelectSearchAsync };
